import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import { todayISO } from '../../utils/dates.js'
import SectionWelcome from '../../components/SectionWelcome.jsx'
import FilterBar from '../../components/FilterBar.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const TABS = [
  { id: 'write',   label: 'Write' },
  { id: 'prompts', label: 'Prompts' },
  { id: 'entries', label: 'Entries' },
]

const MOODS = [
  { id: 'great',       icon: '🌟', label: 'Great' },
  { id: 'good',        icon: '🙂', label: 'Good' },
  { id: 'okay',        icon: '😐', label: 'Okay' },
  { id: 'low',         icon: '😔', label: 'Low' },
  { id: 'overwhelmed', icon: '🌊', label: 'A lot' },
]

const PROMPT_CATEGORIES = [
  {
    id: 'reflection',
    label: 'Reflection',
    icon: '🌿',
    prompts: [
      "What was the most meaningful moment of the past week, and why did it stay with you?",
      "Describe a small thing that brought you unexpected joy recently.",
      "What is something you have learned about yourself this month?",
      "Write about a decision you made recently. What led you there, and how do you feel about it now?",
      "What does your life look like right now compared to one year ago?",
      "Is there something you have been avoiding thinking about? Let yourself approach it gently here.",
      "What would you tell your younger self about this season of life?",
    ],
  },
  {
    id: 'gratitude',
    label: 'Gratitude',
    icon: '✨',
    prompts: [
      "Write about three things you are grateful for today — and why each one matters.",
      "Describe a person who has positively shaped who you are. What would you want them to know?",
      "What is something ordinary about your life that you might be taking for granted?",
      "Write about a place that brings you peace. What does it give you?",
      "What small pleasure do you keep returning to? Why does it mean something to you?",
      "Think of a challenge you have overcome. What are you grateful for in that experience now?",
    ],
  },
  {
    id: 'growth',
    label: 'Growth',
    icon: '🌱',
    prompts: [
      "What do you want to feel more of in your life? What might help you get there?",
      "Describe one habit you would like to let go of — and one you would like to build.",
      "What does your best self look like? What does she do, think, and feel in a typical day?",
      "What is one small step you could take this week toward something that genuinely matters to you?",
      "What fear is currently holding you back? What would you do if it were not there?",
      "Where are you being too hard on yourself? Try writing with compassion instead.",
      "What does success mean to you right now — not in general, but specifically, today?",
    ],
  },
  {
    id: 'feelings',
    label: 'Feelings',
    icon: '🌊',
    prompts: [
      "How are you really feeling today? Give yourself the space to be completely honest.",
      "Is there something you have been carrying that you have not fully acknowledged? Write it out.",
      "Write about a feeling you do not yet have good words for.",
      "When do you feel most like yourself? When do you feel furthest away from who you are?",
      "What does rest mean to you? Are you getting enough of it?",
      "Write a letter to your worry, your grief, or your anxiety — as if it were a person you could speak to.",
    ],
  },
  {
    id: 'creative',
    label: 'Creative',
    icon: '🎨',
    prompts: [
      "Describe your ideal day from beginning to end. Leave nothing out.",
      "Write about a place you have never been but have always imagined visiting.",
      "If your life were a novel, what chapter are you currently in?",
      "What would you do if you knew it would work out?",
      "Write about something you believe that most people around you do not.",
      "Describe the last time you felt genuinely, completely free.",
    ],
  },
]

function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0
}

function formatDate(iso) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

function parseTags(raw) {
  return raw.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean)
}

// ── Write tab ─────────────────────────────────────────────────────────────────

function WriteSection({ mode, setMode, selectedPrompt, setSelectedPrompt, draft, setDraft, mood, setMood, tagsRaw, setTagsRaw, onSave }) {
  const wc = wordCount(draft)

  return (
    <div>
      {/* Mode switcher */}
      <div className="journal-mode-switcher">
        <button
          className={`journal-mode-btn${mode === 'canvas' ? ' active' : ''}`}
          onClick={() => setMode('canvas')}
        >
          Canvas
        </button>
        <button
          className={`journal-mode-btn${mode === 'structured' ? ' active' : ''}`}
          onClick={() => setMode('structured')}
        >
          Structured
        </button>
      </div>

      {/* Selected prompt */}
      {selectedPrompt && (
        <div className="journal-prompt-display" style={{ marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--stone)', display: 'block', marginBottom: 5 }}>Prompt</span>
          {selectedPrompt}
          <button
            className="journal-prompt-clear"
            onClick={() => setSelectedPrompt(null)}
            title="Clear prompt"
          >✕</button>
        </div>
      )}

      {/* Structured fields */}
      {mode === 'structured' && (
        <div className="journal-structured-fields">
          <div className="journal-field-label">How are you feeling?</div>
          <div className="journal-mood-row">
            {MOODS.map(m => (
              <button
                key={m.id}
                className={`journal-mood-btn${mood === m.id ? ' active' : ''}`}
                onClick={() => setMood(prev => prev === m.id ? null : m.id)}
                title={m.label}
              >
                <span className="journal-mood-icon">{m.icon}</span>
                <span className="journal-mood-label">{m.label}</span>
              </button>
            ))}
          </div>

          <div className="journal-field-label" style={{ marginTop: '0.9rem' }}>Tags</div>
          <input
            className="journal-tags-input"
            placeholder="#grateful, #work, #family…"
            value={tagsRaw}
            onChange={e => setTagsRaw(e.target.value)}
          />
        </div>
      )}

      {/* Writing area */}
      <textarea
        className="journal-textarea"
        placeholder={selectedPrompt ? "Write your response here…" : mode === 'structured' ? "What's on your mind today?" : "Write freely…"}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        style={{ minHeight: mode === 'canvas' ? 300 : 200 }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span className="journal-word-count">{wc} {wc === 1 ? 'word' : 'words'}</span>
        <button
          className="save-btn ready"
          onClick={onSave}
          disabled={!draft.trim()}
          style={{ opacity: draft.trim() ? 1 : 0.45, cursor: draft.trim() ? 'pointer' : 'default' }}
        >
          Save entry
        </button>
      </div>
    </div>
  )
}

// ── Prompts tab ───────────────────────────────────────────────────────────────

function PromptsSection({ promptCat, setPromptCat, selectedPrompt, onSelectPrompt }) {
  const category = PROMPT_CATEGORIES.find(c => c.id === promptCat)

  return (
    <>
      <FilterBar
        filters={PROMPT_CATEGORIES.map(c => ({ key: c.id, label: c.label, icon: c.icon }))}
        active={promptCat}
        onChange={setPromptCat}
      />

      <p style={{ fontSize: '0.82rem', color: 'var(--stone)', marginBottom: '0.75rem', lineHeight: 1.55 }}>
        Choose a prompt to start writing — or go to Write and begin with a blank page.
      </p>

      <div className="journal-prompt-list">
        {category.prompts.map((p, i) => (
          <button
            key={i}
            className={`journal-prompt-item${selectedPrompt === p ? ' selected' : ''}`}
            onClick={() => onSelectPrompt(p)}
          >
            {p}
          </button>
        ))}
      </div>
    </>
  )
}

// ── Entries tab ───────────────────────────────────────────────────────────────

function EntriesSection({ entries, onDelete }) {
  const [expanded,       setExpanded]       = useState(new Set())
  const [deleteTarget,   setDeleteTarget]   = useState(null)

  function toggle(id) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (!entries.length) {
    return (
      <p style={{ fontSize: '0.92rem', color: 'var(--stone)', fontStyle: 'italic', lineHeight: 1.75, fontFamily: 'var(--font-serif)' }}>
        No entries yet — go to Write to save your first one.
      </p>
    )
  }

  const moodMap = Object.fromEntries(MOODS.map(m => [m.id, m]))

  return (
    <>
      <div>
        {entries.map(entry => {
          const isOpen = expanded.has(entry.id)
          const m = entry.mood ? moodMap[entry.mood] : null
          const tags = entry.tags?.length ? entry.tags : []

          return (
            <div key={entry.id} className="journal-entry">
              <div className="journal-entry-header" onClick={() => toggle(entry.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                  {m && <span title={m.label} style={{ fontSize: '1rem', flexShrink: 0 }}>{m.icon}</span>}
                  <span className="journal-entry-date">{formatDate(entry.date)}</span>
                  {tags.length > 0 && (
                    <div className="journal-entry-tags">
                      {tags.slice(0, 3).map(t => (
                        <span key={t} className="journal-entry-tag">#{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                  <span className="journal-entry-meta">{entry.wordCount} words</span>
                  <button
                    onClick={e => { e.stopPropagation(); setDeleteTarget(entry.id) }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--stone)', fontSize: '0.7rem', padding: '0 2px',
                    }}
                    title="Delete entry"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {isOpen ? (
                <>
                  {entry.prompt && (
                    <div className="journal-entry-prompt-ref">Prompt: {entry.prompt}</div>
                  )}
                  <p className="journal-entry-body" style={{ marginTop: entry.prompt ? '0.65rem' : 0 }}>
                    {entry.text}
                  </p>
                </>
              ) : (
                <p className="journal-entry-preview">{entry.text}</p>
              )}
            </div>
          )
        })}
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete this entry?"
        message="This entry will be permanently removed."
        onConfirm={() => { onDelete(deleteTarget); setDeleteTarget(null) }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}

// ── Main Journaling page ──────────────────────────────────────────────────────

export default function Journaling() {
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    const t = p.get('tab')
    return TABS.find(x => x.id === t) ? t : 'write'
  })
  const [mode, setMode] = useState(() =>
    localStorage.getItem('journal-mode') === 'structured' ? 'structured' : 'canvas'
  )
  const [entries,        setEntries]        = useState([])
  const [promptCat,      setPromptCat]      = useState('reflection')
  const [selectedPrompt, setSelectedPrompt] = useState(null)
  const [draft,          setDraft]          = useState('')
  const [mood,           setMood]           = useState(null)
  const [tagsRaw,        setTagsRaw]        = useState('')

  useEffect(() => {
    api.getJournalEntries().then(d => setEntries(d || []))
  }, [])

  function goTab(id) {
    setTab(id)
    history.replaceState(null, '', `?tab=${id}`)
  }

  function changeMode(m) {
    setMode(m)
    localStorage.setItem('journal-mode', m)
  }

  function handleSelectPrompt(p) {
    setSelectedPrompt(p)
    goTab('write')
  }

  function saveEntry() {
    const text = draft.trim()
    if (!text) return
    const tags = tagsRaw.trim() ? parseTags(tagsRaw) : []
    const entry = {
      id:        Date.now(),
      date:      todayISO(),
      text,
      prompt:    selectedPrompt || null,
      wordCount: wordCount(text),
      mood:      mood || null,
      tags:      tags.length ? tags : null,
      mode,
    }
    const next = [entry, ...entries]
    setEntries(next)
    api.saveJournalEntries(next)
    setDraft('')
    setSelectedPrompt(null)
    setMood(null)
    setTagsRaw('')
  }

  function deleteEntry(id) {
    const next = entries.filter(e => e.id !== id)
    setEntries(next)
    api.saveJournalEntries(next)
  }

  return (
    <div className="page active">
      <div className="page-inner">
        <div className="hero">
          <p className="hero-eyebrow">My Life</p>
          <h1>Journaling</h1>
          <hr className="hero-rule" />
          <SectionWelcome text="A space entirely your own. Write freely, reflect gently, and come back whenever you need." />
        </div>

        <div className="cooking-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`cooking-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => goTab(t.id)}
            >
              {t.label}
              {t.id === 'entries' && entries.length > 0 && (
                <span style={{ marginLeft: 5, fontSize: '0.65rem', color: 'var(--stone)' }}>
                  ({entries.length})
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'write' && (
          <WriteSection
            mode={mode}
            setMode={changeMode}
            selectedPrompt={selectedPrompt}
            setSelectedPrompt={setSelectedPrompt}
            draft={draft}
            setDraft={setDraft}
            mood={mood}
            setMood={setMood}
            tagsRaw={tagsRaw}
            setTagsRaw={setTagsRaw}
            onSave={saveEntry}
          />
        )}

        {tab === 'prompts' && (
          <PromptsSection
            promptCat={promptCat}
            setPromptCat={setPromptCat}
            selectedPrompt={selectedPrompt}
            onSelectPrompt={handleSelectPrompt}
          />
        )}

        {tab === 'entries' && (
          <EntriesSection entries={entries} onDelete={deleteEntry} />
        )}
      </div>
    </div>
  )
}
